import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import * as Sentry from '@sentry/react';

// React and React Native Core imports
import Page from '../../atoms/Page';
import Card from '../../atoms/Card';
import Button, { Colors } from '../../atoms/Button';

// 3rd party libraries
import dayjs from 'dayjs';
import { Fzf } from 'fzf';
import _ from 'lodash';
import { useCan, Privilege } from '@feedflo/access-control';

// Our Own imports
import Avatar from '../../atoms/Avatar';
import CollapsibleSection from '../../atoms/CollapsibleSection';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import CopyButton from '../../atoms/CopyButton';
import FeedFloErrorBoundary from '../../organisms/FeedFloErrorBoundary';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import TextInput from '../../atoms/TextInput';
import ToggleAccessButton from '../../molecules/ToggleAccessButton';
import TransportMethodSelector from '../../molecules/TransportMethodSelector/TransportMethodSelector';
import MessageHistoryLink from '../../molecules/MessageHistoryLink';

// Relative path imports
import './UserDetailPage.scss';
import useUser from '../../utils/hooks/useUser';
import AddBarnAccessForm from './AddBarnAccessForm';
import useFeature from '../../utils/hooks/useFeature';
import AddOrgAccessForm from './AddOrgAccessForm';

const USER_DETAIL_QUERY = gql`
  query UserDetailPage_getUser($userId: uuid!) {
    user_by_pk(id: $userId) {
      id
      name
      email
      phone_number
      avatar
      user_barns {
        created_at
        updated_at
        deleted_at
        barn {
          id
          name
          address
          organization {
            id
            name
          }
        }
        role {
          id
          name
        }
        user_barn_notification_tier_assignment {
          tier {
            id
            name
          }
        }
      }
      user_organizations {
        created_at
        updated_at
        deleted_at
        organization {
          id
          name
        }
        role {
          id
          name
        }
        user_organization_notification_tier_assignment {
          tier {
            id
            name
          }
        }
      }
      feature_roles {
        created_at
        updated_at
        deleted_at
        role {
          id
          name
          privileges
        }
      }
      global_roles {
        created_at
        updated_at
        deleted_at
        role {
          id
          name
          privileges
        }
      }
      user_barn_notification_tier_assignments {
        id
        barn_id
        tier {
          id
          name
        }
        barn {
          id
          name
          address
          organization {
            id
            name
          }
        }
      }
    }
  }
`;

const SOFT_DELETE_USER_BARN_GQL = gql`
  mutation softDeleteUserBarn($user_id: uuid!, $barn_id: uuid!, $role_id: uuid!, $now: bigint) {
    update_user_barn(
      where: { user_id: { _eq: $user_id }, barn_id: { _eq: $barn_id }, role_id: { _eq: $role_id } }
      _set: { deleted_at: $now }
    ) {
      affected_rows
      returning {
        deleted_at
      }
    }
    delete_user_barn_notification_tier_assignment(where: { user_id: { _eq: $user_id }, barn_id: { _eq: $barn_id } }) {
      affected_rows
    }
  }
`;

const SOFT_DELETE_USER_ORGANIZATION_GQL = gql`
  mutation softDeleteUserOrganization($user_id: uuid!, $organization_id: uuid!, $role_id: uuid!, $now: bigint) {
    update_user_organization(
      where: { user_id: { _eq: $user_id }, organization_id: { _eq: $organization_id }, role_id: { _eq: $role_id } }
      _set: { deleted_at: $now }
    ) {
      affected_rows
      returning {
        deleted_at
      }
    }
  }
`;

const REACTIVATE_USER_BARN_GQL = gql`
  mutation reactivateUserBarn($user_id: uuid!, $barn_id: uuid!, $role_id: uuid!) {
    update_user_barn(
      where: { user_id: { _eq: $user_id }, barn_id: { _eq: $barn_id }, role_id: { _eq: $role_id } }
      _set: { deleted_at: null }
    ) {
      affected_rows
      returning {
        deleted_at
      }
    }
  }
`;

const REACTIVATE_USER_ORGANIZATION_GQL = gql`
  mutation reactivateUserOrganization($user_id: uuid!, $organization_id: uuid!, $role_id: uuid!) {
    update_user_organization(
      where: { user_id: { _eq: $user_id }, organization_id: { _eq: $organization_id }, role_id: { _eq: $role_id } }
      _set: { deleted_at: null }
    ) {
      affected_rows
      returning {
        deleted_at
      }
    }
  }
`;

// Cannot update email as you would need to verify the email to do that?
const UPDATE_USER_GQL = gql`
  mutation UpdateUser($userID: uuid!, $name: String, $phoneNumber: String) {
    update_user_by_pk(pk_columns: { id: $userID }, _set: { name: $name, phone_number: $phoneNumber }) {
      id
      name
      email
      phone_number
    }
  }
`;

const asHasuraUserRole = {
  headers: {
    'x-hasura-role': 'user',
  },
};

function UserDetailPage({ titleSegments = [], hidePageTitleText = false, orgId = null }) {
  // --- State hooks ---
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [orgSortField, setOrgSortField] = useState('name');
  const [orgSortDirection, setOrgSortDirection] = useState('asc');
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [removeType, setRemoveType] = useState(null); // 'barn' or 'organization'
  const [reactivatingItems, setReactivatingItems] = useState(new Set());
  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false);
  const [hideRemovedBarnAccess, setHideRemovedBarnAccess] = useState(true);
  const [hideRemovedOrgAccess, setHideRemovedOrgAccess] = useState(true);
  const [testConfirmationOpen, setTestConfirmationOpen] = useState(false);
  const [pendingTestTransport, setPendingTestTransport] = useState(null);
  const [pendingTestDetails, setPendingTestDetails] = useState(null);

  // --- Route params ---
  const { id: userId } = useParams();

  // -- Privileges, Permissions, and Features--
  const { user: loggedInUser } = useUser();
  const { active: showStaffUI } = useFeature('SHOW_STAFF_ONLY_ELEMENTS');
  const { canAnywhere, can } = useCan();
  const canManageGlobalRoles = canAnywhere('feature', Privilege.USER_FEATURE_ACCESS_MANAGE);
  const canManageFeatureAccess = canAnywhere('feature', Privilege.USER_FEATURE_ACCESS_MANAGE);

  // --- Apollo/GraphQL hooks ---
  const {
    loading,
    error,
    data,
    refetch: refetchUserData,
  } = useQuery(USER_DETAIL_QUERY, {
    variables: { userId },
    context: asHasuraUserRole,
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const [softDeleteUserBarn] = useMutation(SOFT_DELETE_USER_BARN_GQL, {
    refetchQueries: [{ query: USER_DETAIL_QUERY, variables: { userId }, context: asHasuraUserRole }],
    context: asHasuraUserRole,
  });

  const [softDeleteUserOrganization] = useMutation(SOFT_DELETE_USER_ORGANIZATION_GQL, {
    refetchQueries: [{ query: USER_DETAIL_QUERY, variables: { userId }, context: asHasuraUserRole }],
    context: asHasuraUserRole,
  });

  const [reactivateUserBarn] = useMutation(REACTIVATE_USER_BARN_GQL, {
    refetchQueries: [{ query: USER_DETAIL_QUERY, variables: { userId }, context: asHasuraUserRole }],
    context: asHasuraUserRole,
  });

  const [reactivateUserOrganization] = useMutation(REACTIVATE_USER_ORGANIZATION_GQL, {
    refetchQueries: [{ query: USER_DETAIL_QUERY, variables: { userId }, context: asHasuraUserRole }],
    context: asHasuraUserRole,
  });

  const [updateUser] = useMutation(UPDATE_USER_GQL, {
    refetchQueries: [
      { query: USER_DETAIL_QUERY, variables: { userId }, context: asHasuraUserRole },
      'GetUserContactInfo',
    ],
    context: asHasuraUserRole,
  });

  // --- Derived state ---
  const pageTitleSegments = useMemo(
    () => [data?.user_by_pk?.name || 'User', ...titleSegments],
    [data?.user_by_pk?.name, titleSegments],
  );
  const user = data?.user_by_pk;
  const userBarns = user?.user_barns || [];
  const loggedInUserHasMultiOrgAccess = loggedInUser?.user_organization?.length > 1;
  const userOrganizations = user?.user_organizations || [];
  const userFeatureRoles = user?.feature_roles || [];
  const userGlobalRoles = user?.global_roles || [];
  const safelyDisplayStaffUI = loggedInUser?.isStaff && showStaffUI;

  // Map organization IDs to their tier assignments for inherited tier lookup
  const orgTierMap = useMemo(() => {
    const map = {};
    userOrganizations.forEach((userOrg) => {
      if (userOrg?.organization?.id) {
        const assignment = Array.isArray(userOrg.user_organization_notification_tier_assignment)
          ? userOrg.user_organization_notification_tier_assignment[0]
          : userOrg.user_organization_notification_tier_assignment;
        map[userOrg.organization.id] = assignment?.tier?.name;
      }
    });
    return map;
  }, [userOrganizations]);

  const orgRoleMap = useMemo(() => {
    const map = {};
    userOrganizations
      .filter((userOrg) => !userOrg.deleted_at)
      .forEach((userOrg) => {
        if (userOrg?.organization?.id) {
          map[userOrg.organization.id] = userOrg.role;
        }
      });
    return map;
  }, [userOrganizations]);

  const allBarnAccessRows = useMemo(() => {
    const rows = [...userBarns.filter((userBarn) => userBarn?.barn)];
    const activeBarnIdsWithRole = new Set(
      userBarns.filter((userBarn) => !userBarn.deleted_at && userBarn?.barn).map((userBarn) => userBarn.barn.id),
    );

    (user?.user_barn_notification_tier_assignments || []).forEach((assignment) => {
      if (!assignment.barn || activeBarnIdsWithRole.has(assignment.barn_id)) return;

      const orgId = assignment.barn?.organization?.id;
      const inheritedOrgRole = orgId ? orgRoleMap[orgId] : null;
      if (!inheritedOrgRole) return;

      rows.push({
        isTierOnly: true,
        deleted_at: null,
        created_at: null,
        updated_at: null,
        barn: assignment.barn,
        role: null,
        inheritedOrgRole,
        user_barn_notification_tier_assignment: { tier: assignment.tier },
      });
    });

    return rows;
  }, [userBarns, user, orgRoleMap]);

  // Process and filter user barns
  const filteredAndSortedUserBarns = useMemo(() => {
    if (!allBarnAccessRows.length) return [];

    let filtered = allBarnAccessRows;

    // Filter out removed barn access if hideRemovedBarnAccess is true
    if (hideRemovedBarnAccess) {
      filtered = filtered.filter((userBarn) => !userBarn.deleted_at);
    }

    // Apply search filter
    if (searchTerm) {
      const fzf = new Fzf(filtered, { selector: (item) => `${item.barn.name} ${item.barn.address}` });
      filtered = fzf?.find(searchTerm)?.map((e) => e.item);
    }

    // Apply sorting
    if (sortField === 'name') {
      filtered = _.orderBy(
        filtered,
        [(userBarn) => userBarn.barn.name, (userBarn) => userBarn.created_at],
        [sortDirection, 'asc'],
      );
    } else if (sortField === 'address') {
      filtered = _.orderBy(
        filtered,
        [(userBarn) => userBarn.barn.address, (userBarn) => userBarn.created_at],
        [sortDirection, 'asc'],
      );
    } else if (sortField === 'access_granted') {
      filtered = _.orderBy(filtered, [(userBarn) => userBarn.created_at], [sortDirection]);
    } else if (sortField === 'role') {
      filtered = _.orderBy(
        filtered,
        [
          (userBarn) => userBarn.role?.name || userBarn.inheritedOrgRole?.name || '',
          (userBarn) => userBarn.created_at,
        ],
        [sortDirection, 'asc'],
      );
    }

    return filtered;
  }, [allBarnAccessRows, searchTerm, sortField, sortDirection, hideRemovedBarnAccess]);

  const hasMultipleOrgs = new Set(allBarnAccessRows?.map((ub) => ub?.barn?.organization?.id)).size > 1;

  // Process and filter user organizations
  const filteredAndSortedUserOrgs = useMemo(() => {
    if (!userOrganizations) return [];

    let filtered = userOrganizations;

    // Filter out removed organization access if hideRemovedOrgAccess is true
    if (hideRemovedOrgAccess) {
      filtered = filtered.filter((userOrg) => !userOrg.deleted_at);
    }

    // Apply search filter
    if (orgSearchTerm) {
      const fzf = new Fzf(filtered, { selector: (item) => item.organization.name });
      filtered = fzf?.find(orgSearchTerm)?.map((e) => e.item);
    }

    // Apply sorting
    if (orgSortField === 'name') {
      filtered = _.orderBy(filtered, [(userOrg) => userOrg.organization?.name], [orgSortDirection]);
    } else if (orgSortField === 'access_granted') {
      filtered = _.orderBy(filtered, [(userOrg) => userOrg.created_at], [orgSortDirection]);
    } else if (orgSortField === 'role') {
      filtered = _.orderBy(filtered, [(userOrg) => userOrg.role?.name || ''], [orgSortDirection]);
    }

    return filtered;
  }, [userOrganizations, orgSearchTerm, orgSortField, orgSortDirection, hideRemovedOrgAccess]);

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle organization sort column click
  const handleOrgSort = (field) => {
    if (orgSortField === field) {
      setOrgSortDirection(orgSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrgSortField(field);
      setOrgSortDirection('asc');
    }
  };

  // --- Function definitions ---
  const handleRemoveBarnAccess = (userBarn) => {
    setItemToRemove(userBarn);
    setRemoveType('barn');
    setConfirmationDialogOpen(true);
  };

  const handleRemoveOrgAccess = (userOrg) => {
    setItemToRemove(userOrg);
    setRemoveType('organization');
    setConfirmationDialogOpen(true);
  };

  const handleReactivateBarnAccess = async (userBarn) => {
    const itemKey = `barn-${userBarn.barn.id}-${userBarn?.role?.id}`;
    if (reactivatingItems.has(itemKey)) return;

    setReactivatingItems((prev) => new Set(prev).add(itemKey));

    try {
      await reactivateUserBarn({
        variables: {
          user_id: userId,
          barn_id: userBarn.barn.id,
          role_id: userBarn?.role?.id,
        },
      });
      toast.success('Barn access successfully reactivated', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error) {
      console.error('Failed to reactivate barn access:', error);
      toast.warn('Error occurred while reactivating barn access', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setReactivatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemKey);
        return newSet;
      });
    }
  };

  const handleReactivateOrgAccess = async (userOrg) => {
    const itemKey = `org-${userOrg.organization.id}=${userOrg?.role.id}`;
    if (reactivatingItems.has(itemKey)) return;

    setReactivatingItems((prev) => new Set(prev).add(itemKey));

    try {
      await reactivateUserOrganization({
        variables: {
          user_id: userId,
          organization_id: userOrg.organization.id,
          role_id: userOrg?.role.id,
        },
      });
      toast.success('Organization access successfully reactivated', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error) {
      console.error('Failed to reactivate organization access:', error);
      toast.warn('Error occurred while reactivating organization access', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setReactivatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemKey);
        return newSet;
      });
    }
  };

  const handleConfirmRemove = async () => {
    try {
      if (removeType === 'barn') {
        await softDeleteUserBarn({
          variables: {
            user_id: userId,
            barn_id: itemToRemove.barn.id,
            role_id: itemToRemove?.role.id,
            now: dayjs().unix(),
          },
        });
        toast.success('Barn access successfully removed', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else if (removeType === 'organization') {
        await softDeleteUserOrganization({
          variables: {
            user_id: userId,
            organization_id: itemToRemove.organization.id,
            role_id: itemToRemove?.role.id,
            now: dayjs().unix(),
          },
        });
        toast.success('Organization access successfully removed', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (error) {
      console.error('Failed to remove access:', error);
      toast.warn('Error occurred while removing access', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }

    setConfirmationDialogOpen(false);
    setItemToRemove(null);
    setRemoveType(null);
  };

  const handleCancelRemove = () => {
    setConfirmationDialogOpen(false);
    setItemToRemove(null);
    setRemoveType(null);
  };

  // --- Personal Info Form Functions ---
  const handleEditPersonalInfo = () => {
    setIsEditingPersonalInfo(true);
  };

  const handleCancelEdit = () => {
    setIsEditingPersonalInfo(false);
  };

  const handleSavePersonalInfo = async (values, { setSubmitting }) => {
    try {
      const result = await updateUser({
        variables: {
          userID: userId,
          name: values.name,
          phoneNumber: values.phone_number,
        },
      });

      if (result.data?.update_user_by_pk) {
        toast.success('Personal information updated successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        setIsEditingPersonalInfo(false);
      } else {
        console.warn('Update returned null:', result);
        throw new Error('User update failed - no data returned');
      }
    } catch (error) {
      console.error('Failed to update personal information:', error);
      Sentry.captureException(error);
      toast.error('Failed to update personal information', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle test notification confirmation
  const handleBeforeTest = async (transportId, transportDetails) => {

    // For other users, show confirmation
    setPendingTestTransport(transportId);
    setPendingTestDetails(transportDetails);
    setTestConfirmationOpen(true);

    // Return false to prevent immediate test, wait for confirmation
    return new Promise((resolve) => {
      // Store the resolve function so we can call it later
      window.__testConfirmationResolve = resolve;
    });
  };

  const handleConfirmTest = () => {
    setTestConfirmationOpen(false);
    if (window.__testConfirmationResolve) {
      window.__testConfirmationResolve(true);
      delete window.__testConfirmationResolve;
    }
    setPendingTestTransport(null);
    setPendingTestDetails(null);
  };

  const handleCancelTest = () => {
    setTestConfirmationOpen(false);
    if (window.__testConfirmationResolve) {
      window.__testConfirmationResolve(false);
      delete window.__testConfirmationResolve;
    }
    setPendingTestTransport(null);
    setPendingTestDetails(null);
  };

  // Get transport label and contact info for confirmation message
  const getTestConfirmationMessage = () => {
    if (!pendingTestTransport || !user) return null;

    const transportLabels = {
      sms: 'SMS',
      email: 'email',
      push: 'push notification',
    };

    // Get contact info, using transport details if available (especially for push device label)
    let contact;
    if (pendingTestTransport === 'sms') {
      contact = user.phone_number || 'unknown number';
    } else if (pendingTestTransport === 'email') {
      contact = user.email || 'unknown email';
    } else if (pendingTestTransport === 'push') {
      contact = pendingTestDetails?.targetAddress || 'mobile device';
    }

    const showContactPill =
      pendingTestTransport === 'sms' || pendingTestTransport === 'email' || pendingTestTransport === 'push';

    return (
      <div className="UserDetail-TestConfirmation">
        <div className="UserDetail-TestConfirmationText">Confirm test {transportLabels[pendingTestTransport]} to</div>
        <div className="UserDetail-TestConfirmationPill">{user.name}</div>
        {showContactPill && (
          <>
            <div className="UserDetail-TestConfirmationPill">({contact})</div>
          </>
        )}
      </div>
    );
  };

  const getTestConfirmButtonText = () => {
    if (!pendingTestTransport) return 'Send Message';

    const buttonLabels = {
      sms: 'Send SMS',
      email: 'Send Email',
      push: 'Send Notification',
    };

    return buttonLabels[pendingTestTransport] || 'Send Message';
  };

  if (error) throw error;

  // Simple error/feedback
  if (!user && !loading) {
    return (
      <Page className="UserDetailPage" titleSegments={['Users', 'User Not Found']}>
        <div className="UserDetail-Content">
          <Card className="UserNotFound" role="alert">
            <h2 className="UserNotFound-Heading">User Not Found</h2>
            <p>The user you&apos;re looking for doesn&apos;t exist or may have been removed.</p>
          </Card>
        </div>
      </Page>
    );
  }

  if (loading) {
    // cursor-generated, looks good enough
    return (
      <Page className="UserDetailPage" titleSegments={pageTitleSegments}>
        <div className="UserDetail-Content">
          <div className="UserDetail-Header"></div>
          <CollapsibleSection title="Personal Information" isExpanded={true}>
            <Card className="UserDetail-Card">
              <div className="UserDetail-PersonalInfo">
                <div className="UserDetail-AvatarSection">
                  <LoadingSkeleton
                    className="UserDetail-AvatarSkeleton"
                    style={{ width: '80px', height: '80px', borderRadius: '50%' }}
                  />
                </div>
                <div className="UserDetail-InfoSection">
                  <div className="UserDetail-Form">
                    <div className="UserDetail-FormRow">
                      <LoadingSkeleton className="UserDetail-LabelSkeleton" style={{ width: '80px', height: '16px' }} />
                      <LoadingSkeleton
                        className="UserDetail-InputSkeleton"
                        style={{ width: '200px', height: '32px' }}
                      />
                    </div>
                    <div className="UserDetail-FormRow">
                      <LoadingSkeleton className="UserDetail-LabelSkeleton" style={{ width: '80px', height: '16px' }} />
                      <LoadingSkeleton
                        className="UserDetail-InputSkeleton"
                        style={{ width: '200px', height: '32px' }}
                      />
                    </div>
                    <div className="UserDetail-FormRow">
                      <LoadingSkeleton className="UserDetail-LabelSkeleton" style={{ width: '80px', height: '16px' }} />
                      <LoadingSkeleton
                        className="UserDetail-InputSkeleton"
                        style={{ width: '200px', height: '32px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </CollapsibleSection>

          <CollapsibleSection title="Barn Access" isExpanded={true}>
            <Card className="UserDetail-Card">
              <div className="UserDetail-BarnAccess">
                <div className="UserDetail-SearchSection">
                  <LoadingSkeleton className="UserDetail-SearchSkeleton" style={{ width: '300px', height: '32px' }} />
                </div>
                <div className="UserDetail-TableSection">
                  {Array.from({ length: 3 }, (_, index) => (
                    <div key={index} className="UserDetail-TableRow">
                      <LoadingSkeleton
                        className="UserDetail-TableCellSkeleton"
                        style={{ width: '150px', height: '20px' }}
                      />
                      <LoadingSkeleton
                        className="UserDetail-TableCellSkeleton"
                        style={{ width: '200px', height: '20px' }}
                      />
                      <LoadingSkeleton
                        className="UserDetail-TableCellSkeleton"
                        style={{ width: '100px', height: '20px' }}
                      />
                      <LoadingSkeleton
                        className="UserDetail-TableCellSkeleton"
                        style={{ width: '80px', height: '32px' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </CollapsibleSection>

          <CollapsibleSection title="Organization Access" isExpanded={true}>
            <Card className="UserDetail-Card">
              <div className="UserDetail-OrgAccess">
                <div className="UserDetail-SearchSection">
                  <LoadingSkeleton className="UserDetail-SearchSkeleton" style={{ width: '300px', height: '32px' }} />
                </div>
                <div className="UserDetail-TableSection">
                  {Array.from({ length: 2 }, (_, index) => (
                    <div key={index} className="UserDetail-TableRow">
                      <LoadingSkeleton
                        className="UserDetail-TableCellSkeleton"
                        style={{ width: '150px', height: '20px' }}
                      />
                      <LoadingSkeleton
                        className="UserDetail-TableCellSkeleton"
                        style={{ width: '100px', height: '20px' }}
                      />
                      <LoadingSkeleton
                        className="UserDetail-TableCellSkeleton"
                        style={{ width: '80px', height: '32px' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </CollapsibleSection>
        </div>
      </Page>
    );
  }

  return (
    <FeedFloErrorBoundary>
      <Page className="UserDetailPage" titleSegments={pageTitleSegments} heading={hidePageTitleText ? '' : user?.name}>
        <div className="UserDetail-Content">
          <div className="UserDetail-Header"></div>

          <CollapsibleSection title="Personal Information" isExpanded={true}>
            <Card className="UserDetail-Card">
              <div className="UserDetail-PersonalInfo">
                <div className="UserDetail-AvatarSection">
                  <Avatar src={user.avatar} alt={user.name} size="large" />
                </div>
                <div className="UserDetail-InfoSection">
                  <Formik
                    initialValues={{
                      name: user.name || '',
                      email: user.email || '',
                      phone_number: user.phone_number || '',
                    }}
                    enableReinitialize
                    onSubmit={handleSavePersonalInfo}
                  >
                    {({ values, handleChange, handleBlur, isSubmitting, dirty }) => (
                      <Form className="UserDetail-Form">
                        <TextInput
                          className="UserDetail-FormRow"
                          name="name"
                          label="Name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          displayOnly={!isEditingPersonalInfo}
                          onClick={safelyDisplayStaffUI ? () => setIsEditingPersonalInfo(true) : null}
                          horizontal={true}
                          placeholder="Enter name"
                        />
                        <TextInput
                          className="UserDetail-FormRow"
                          name="email"
                          label="Email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          displayOnly={true} // Email should always be display only. Changing requires validation
                          onClick={safelyDisplayStaffUI ? () => setIsEditingPersonalInfo(true) : null}
                          horizontal={true}
                          placeholder="Enter email"
                        />
                        <TextInput
                          className="UserDetail-FormRow"
                          name="phone_number"
                          label="Phone"
                          value={values.phone_number}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          displayOnly={!isEditingPersonalInfo}
                          onClick={safelyDisplayStaffUI ? () => setIsEditingPersonalInfo(true) : null}
                          horizontal={true}
                          placeholder="Enter phone number"
                        />
                        {safelyDisplayStaffUI && (
                          <TextInput
                            className="UserDetail-FormRow"
                            label="ID"
                            value={user?.id}
                            displayOnly={true}
                            horizontal={true}
                            right={<CopyButton data={user?.id} />}
                          />
                        )}
                        <div className="UserDetail-FormActions">
                          {
                            safelyDisplayStaffUI ? ( // if staff
                              !isEditingPersonalInfo ? ( // allow editing
                                <Button
                                  content="Edit"
                                  variant="text"
                                  color="success"
                                  onClick={handleEditPersonalInfo}
                                />
                              ) : (
                                <div className="UserDetail-FormActionButtons">
                                  <Button
                                    type="submit"
                                    content="Save"
                                    variant="text"
                                    color="success"
                                    disabled={isSubmitting || !dirty}
                                  />
                                  <Button
                                    content="Cancel"
                                    variant="vivid"
                                    color="secondary"
                                    onClick={handleCancelEdit}
                                    disabled={isSubmitting}
                                  />
                                </div>
                              )
                            ) : (
                              <></>
                            ) //else no editing
                          }
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </Card>
          </CollapsibleSection>

          <CollapsibleSection title="Notification Preferences" isExpanded={true}>
            <Card className="UserDetail-Card">
              <div className="UserDetail-AlertSettings">
                <MessageHistoryLink
                  filters={{ userId, ...(orgId ? { orgId } : {}) }}
                  label={orgId ? undefined : 'Message history for this user'}
                />
                <TransportMethodSelector
                  showDetails={true}
                  label=""
                  userId={userId}
                  onBeforeTest={handleBeforeTest}
                  autoSave={true}
                />
              </div>
            </Card>
          </CollapsibleSection>

          <CollapsibleSection title="Organization-Wide Barn Access" isExpanded={true}>
            <Card className="UserDetail-Card">
              <div className="UserDetail-OrgAccess">
                <div className="UserDetail-TableControls">
                  <div className="UserDetail-TableControls UserDetail-TableControlsLeft">
                    {loggedInUserHasMultiOrgAccess && userOrganizations?.length > 1 && (
                      <TextInput
                        placeholder="Search Organizations"
                        value={orgSearchTerm}
                        // label={false}
                        onChange={(e) => setOrgSearchTerm(e.target.value)}
                      />
                    )}
                    <Button
                      content={hideRemovedOrgAccess ? 'Show Removed Access' : 'Hide Removed Access'}
                      variant="text"
                      color="secondary"
                      onClick={() => setHideRemovedOrgAccess(!hideRemovedOrgAccess)}
                    />
                  </div>
                  <AddOrgAccessForm
                    userId={userId}
                    orgId={orgId}
                    onSuccess={refetchUserData}
                    userOrganizations={userOrganizations}
                  />
                </div>

                <div className="UserDetail-Table">
                  {filteredAndSortedUserOrgs.length > 0 ? (
                    <div className="UserDetail-OrgTableContainer">
                      <div className="UserDetail-TableHeader">
                        <div className="UserDetail-TableRow">
                          <div
                            className="UserDetail-TableCell UserDetail-TableHeaderCell UserDetail-TableSortableHeader"
                            onClick={() => handleOrgSort('name')}
                          >
                            Organization Name
                            {orgSortField === 'name' && (
                              <span className="UserDetail-SortIndicator">
                                {orgSortDirection === 'asc' ? ' ↑' : ' ↓'}
                              </span>
                            )}
                          </div>
                          <div
                            className="UserDetail-TableCell UserDetail-TableHeaderCell UserDetail-TableSortableHeader"
                            onClick={() => handleOrgSort('access_granted')}
                          >
                            Access Modified Date
                            {orgSortField === 'access_granted' && (
                              <span className="UserDetail-SortIndicator">
                                {orgSortDirection === 'asc' ? ' ↑' : ' ↓'}
                              </span>
                            )}
                          </div>
                          <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Notification Policy</div>
                          <div
                            className="UserDetail-TableCell UserDetail-TableHeaderCell UserDetail-TableSortableHeader"
                            onClick={() => handleOrgSort('role')}
                          >
                            Role
                            {orgSortField === 'role' && (
                              <span className="UserDetail-SortIndicator">
                                {orgSortDirection === 'asc' ? ' ↑' : ' ↓'}
                              </span>
                            )}
                          </div>
                          <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Actions</div>
                        </div>
                      </div>
                      <div className="UserDetail-TableBody">
                        {filteredAndSortedUserOrgs.map((userOrg) => {
                          const isRemoved = !!userOrg.deleted_at;
                          const itemKey = `org-${userOrg.organization.id}-${userOrg?.role.id}`;
                          const canManageOrgAccess = can('org', userOrg.organization.id, Privilege.ORG_USERS_MANAGE);

                          return (
                            <div
                              className={`UserDetail-TableRow ${isRemoved ? 'UserDetail-TableRow--removed' : ''}`}
                              key={itemKey}
                            >
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                <Link to={`/orgs/${userOrg.organization.id}/barns`} className="UserDetail-OrgLink">
                                  {userOrg?.organization?.name}
                                </Link>
                              </div>
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                {dayjs((userOrg.deleted_at ?? userOrg.updated_at) * 1000).format('MMM DD, YYYY')}
                              </div>
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                {(() => {
                                  const assignment = Array.isArray(
                                    userOrg?.user_organization_notification_tier_assignment,
                                  )
                                    ? userOrg.user_organization_notification_tier_assignment[0]
                                    : userOrg?.user_organization_notification_tier_assignment;
                                  return assignment?.tier?.name || '[not set]';
                                })()}
                              </div>
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                {userOrg?.role?.name || 'No role assigned'}{' '}
                              </div>
                              <div className="UserDetail-TableCell">
                                {canManageOrgAccess && (
                                  <ToggleAccessButton
                                    actionType={isRemoved ? 'reactivate' : 'remove'}
                                    onClick={() =>
                                      isRemoved ? handleReactivateOrgAccess(userOrg) : handleRemoveOrgAccess(userOrg)
                                    }
                                    loading={isRemoved && reactivatingItems.has(itemKey)}
                                    text={isRemoved ? 'Reactivate Access' : 'Remove Access'}
                                    title={isRemoved ? 'Reactivate organization access' : 'Remove organization access'}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="UserDetail-NoResults">
                      {orgSearchTerm
                        ? `No organizations found matching "${orgSearchTerm}"`
                        : 'No organization access found'}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </CollapsibleSection>

          <CollapsibleSection title="Explicit Barn Access" isExpanded={true} bodyClassName="UserDetail-barnAccessBody">
            <Card className="UserDetail-Card">
              <div className="UserDetail-BarnAccess">
                <div className="UserDetail-TableControls">
                  <div className="UserDetail-TableControls UserDetail-TableControlsLeft">
                    <TextInput
                      placeholder="Search Barns"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button
                      content={hideRemovedBarnAccess ? 'Show Removed Access' : 'Hide Removed Access'}
                      variant="text"
                      color="secondary"
                      onClick={() => setHideRemovedBarnAccess(!hideRemovedBarnAccess)}
                    />
                  </div>
                  <AddBarnAccessForm userId={userId} onSuccess={refetchUserData} userBarns={userBarns} />
                </div>

                <div className="UserDetail-Table">
                  {filteredAndSortedUserBarns.length > 0 ? (
                    <div className="UserDetail-TableContainer">
                      <div className="UserDetail-TableHeader">
                        <div className="UserDetail-TableRow">
                          <div
                            className="UserDetail-TableCell UserDetail-TableHeaderCell UserDetail-TableSortableHeader"
                            onClick={() => handleSort('name')}
                          >
                            Barn Name
                            {sortField === 'name' && (
                              <span className="UserDetail-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                            )}
                          </div>
                          <div
                            className="UserDetail-TableCell UserDetail-TableHeaderCell UserDetail-TableSortableHeader"
                            onClick={() => handleSort('address')}
                          >
                            Address
                            {sortField === 'address' && (
                              <span className="UserDetail-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                            )}
                          </div>
                          <div
                            className="UserDetail-TableCell UserDetail-TableHeaderCell UserDetail-TableSortableHeader"
                            onClick={() => handleSort('access_granted')}
                          >
                            Access Modified Date
                            {sortField === 'access_granted' && (
                              <span className="UserDetail-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                            )}
                          </div>
                          <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Notification Policy</div>
                          <div
                            className="UserDetail-TableCell UserDetail-TableHeaderCell UserDetail-TableSortableHeader"
                            onClick={() => handleSort('role')}
                          >
                            Role
                            {sortField === 'role' && (
                              <span className="UserDetail-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                            )}
                          </div>
                          <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Actions</div>
                        </div>
                      </div>
                      <div className="UserDetail-TableBody">
                        {filteredAndSortedUserBarns.map((userBarn) => {
                          const isRemoved = !!userBarn.deleted_at;
                          const itemKey = userBarn.isTierOnly
                            ? `barn-tier-${userBarn.barn.id}`
                            : `barn-${userBarn.barn.id}-${userBarn.role?.id}`;

                          const barnAssignment = Array.isArray(userBarn?.user_barn_notification_tier_assignment)
                            ? userBarn.user_barn_notification_tier_assignment[0]
                            : userBarn?.user_barn_notification_tier_assignment;
                          const barnTier = barnAssignment?.tier?.name;
                          const orgId = userBarn.barn?.organization?.id;
                          const orgTier = orgId ? orgTierMap[orgId] : null;
                          const canManageOrgAccess = can('org', orgId, Privilege.ORG_USERS_MANAGE);

                          return (
                            <div
                              className={`UserDetail-TableRow ${isRemoved ? 'UserDetail-TableRow--removed' : ''}`}
                              key={itemKey}
                            >
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                <Link to={`/b/${userBarn.barn.id}/summary`} className="UserDetail-BarnLink">
                                  {userBarn.barn.name}
                                  {hasMultipleOrgs ? ` (${userBarn?.barn?.organization?.name})` : ''}
                                </Link>
                              </div>
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                {userBarn.barn.address || 'No address'}
                              </div>
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                {userBarn.created_at || userBarn.updated_at
                                  ? dayjs((userBarn.deleted_at || userBarn.updated_at) * 1000).format('MMM DD, YYYY')
                                  : '—'}
                              </div>
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                <span className="UserDetail-tierName">{barnTier || orgTier || '[not set]'}</span>
                                <span className="UserDetail-tierInherited">
                                  {orgTier && !barnTier ? '(inherited)' : ''}
                                </span>
                              </div>
                              <div
                                className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                              >
                                {userBarn.isTierOnly && userBarn.inheritedOrgRole
                                  ? `Org: ${userBarn.inheritedOrgRole.name}`
                                  : userBarn.role?.name || 'No role assigned'}
                              </div>
                              <div className="UserDetail-TableCell">
                                {canManageOrgAccess && !userBarn.isTierOnly && (
                                  <ToggleAccessButton
                                    actionType={isRemoved ? 'reactivate' : 'remove'}
                                    onClick={() =>
                                      isRemoved
                                        ? handleReactivateBarnAccess(userBarn)
                                        : handleRemoveBarnAccess(userBarn)
                                    }
                                    loading={isRemoved && reactivatingItems.has(itemKey)}
                                    text={isRemoved ? 'Reactivate Access' : 'Remove Access'}
                                    title={isRemoved ? 'Reactivate barn access' : 'Remove barn access'}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="UserDetail-NoResults">
                      {searchTerm ? `No barns found matching "${searchTerm}"` : 'No barn access found'}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </CollapsibleSection>

          {canManageGlobalRoles && safelyDisplayStaffUI && (
            <CollapsibleSection title="Global Roles Management" isExpanded={true}>
              <Card className="UserDetail-Card">
                <div className="UserDetail-GlobalRoles">
                  <div className="UserDetail-Table">
                    {userGlobalRoles.length > 0 ? (
                      <div className="UserDetail-RoleTableContainer">
                        <div className="UserDetail-TableHeader">
                          <div className="UserDetail-TableRow">
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Role Name</div>
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Privileges</div>
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Access Modified Date</div>
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Actions</div>
                          </div>
                        </div>
                        <div className="UserDetail-TableBody">
                          {userGlobalRoles.map((userGlobalRole) => {
                            const isRemoved = !!userGlobalRole.deleted_at;
                            const itemKey = `global-role-${userGlobalRole.role.id}`;

                            return (
                              <div
                                className={`UserDetail-TableRow ${isRemoved ? 'UserDetail-TableRow--removed' : ''}`}
                                key={itemKey}
                              >
                                <div
                                  className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                                >
                                  {userGlobalRole.role?.name || 'No role assigned'}
                                </div>
                                <div
                                  className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                                >
                                  {userGlobalRole.role?.privileges?.length > 0 ? (
                                    <div className="UserDetail-PrivilegeBubbles">
                                      {userGlobalRole.role.privileges
                                        .slice()
                                        .sort()
                                        .map((privilege) => (
                                          <span
                                            key={`global-role-${userGlobalRole.role.id}-${privilege}`}
                                            className="UserDetail-PrivilegeBubble"
                                          >
                                            {privilege}
                                          </span>
                                        ))}
                                    </div>
                                  ) : (
                                    'No privileges'
                                  )}
                                </div>
                                <div
                                  className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                                >
                                  {dayjs((userGlobalRole.deleted_at || userGlobalRole.updated_at) * 1000).format(
                                    'MMM DD, YYYY',
                                  )}
                                </div>
                                <div className="UserDetail-TableCell">Coming soon</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="UserDetail-NoResults">No global roles found</div>
                    )}
                  </div>
                </div>
              </Card>
            </CollapsibleSection>
          )}

          {canManageFeatureAccess && safelyDisplayStaffUI && (
            <CollapsibleSection title="Feature Access Management" isExpanded={true}>
              <Card className="UserDetail-Card">
                <div className="UserDetail-FeatureAccess">
                  <div className="UserDetail-Table">
                    {userFeatureRoles.length > 0 ? (
                      <div className="UserDetail-RoleTableContainer">
                        <div className="UserDetail-TableHeader">
                          <div className="UserDetail-TableRow">
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Role Name</div>
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Privileges</div>
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Access Modified Date</div>
                            <div className="UserDetail-TableCell UserDetail-TableHeaderCell">Actions</div>
                          </div>
                        </div>
                        <div className="UserDetail-TableBody">
                          {userFeatureRoles.map((userFeatureRole) => {
                            const isRemoved = !!userFeatureRole.deleted_at;
                            const itemKey = `feature-role-${userFeatureRole.role.id}`;

                            return (
                              <div
                                className={`UserDetail-TableRow ${isRemoved ? 'UserDetail-TableRow--removed' : ''}`}
                                key={itemKey}
                              >
                                <div
                                  className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                                >
                                  {userFeatureRole.role?.name || 'No role assigned'}
                                </div>
                                <div
                                  className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                                >
                                  {userFeatureRole.role?.privileges?.length > 0 ? (
                                    <div className="UserDetail-PrivilegeBubbles">
                                      {userFeatureRole.role.privileges
                                        .slice()
                                        .sort()
                                        .map((privilege) => (
                                          <span
                                            key={`feature-role-${userFeatureRole.role.id}-${privilege}`}
                                            className="UserDetail-PrivilegeBubble"
                                          >
                                            {privilege}
                                          </span>
                                        ))}
                                    </div>
                                  ) : (
                                    'No privileges'
                                  )}
                                </div>
                                <div
                                  className={`UserDetail-TableCell ${isRemoved ? 'UserDetail-TableCell--removed' : ''}`}
                                >
                                  {dayjs((userFeatureRole.deleted_at || userFeatureRole.updated_at) * 1000).format(
                                    'MMM DD, YYYY',
                                  )}
                                </div>
                                <div className="UserDetail-TableCell">Coming soon</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="UserDetail-NoResults">No feature roles found</div>
                    )}
                  </div>
                </div>
              </Card>
            </CollapsibleSection>
          )}

          <ConfirmationDialog
            open={confirmationDialogOpen}
            message={`Are you sure you want to remove ${itemToRemove?.barn?.name || itemToRemove?.organization?.name
              } access for ${user?.name}?`}
            onCancel={handleCancelRemove}
            onConfirm={handleConfirmRemove}
          />

          <ConfirmationDialog
            open={testConfirmationOpen}
            message={getTestConfirmationMessage()}
            onCancel={handleCancelTest}
            onConfirm={handleConfirmTest}
            confirmText={getTestConfirmButtonText()}
            confirmColor={Colors.Success}
          />
        </div>
      </Page>
    </FeedFloErrorBoundary>
  );
}

UserDetailPage.propTypes = {
  titleSegments: PropTypes.arrayOf(PropTypes.string),
  hidePageTitleText: PropTypes.bool,
  orgId: PropTypes.string,
};

export default UserDetailPage;
